import { useState } from 'react';
import { IconSparkles } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { PageHeader, useLayout } from '@hana/shell';
import { Skeleton, StateView, useToast } from '@hana/ui';
import { DOC_TYPES, LIB, countByType, filterLib, type DocType } from '../data/library';
import { SearchRow } from '../courses/Courses';
import { useEdu } from '../useEdu';
import s from '../edu.module.css';

// Màn Thư viện (board Mint-Training, app `lib`): SOP · hướng dẫn · video, lọc theo loại, Ask HANA tạo bài kiểm tra từ tài liệu.
// Đầu trang board không có nút chủ chốt cho app này.

type Filter = DocType | 'all';
const FILTERS: readonly { k: Filter; label: string }[] = [{ k: 'all', label: 'Tất cả' }, ...DOC_TYPES.map((k) => ({ k, label: k }))];
const TONE: Record<DocType, string> = { SOP: 'go', 'Hướng dẫn': 'ok', Video: 'warn' };

function LibBody({ q, f }: { q: string; f: Filter }) {
  const t = useT();
  const toast = useToast();
  const [gen, setGen] = useState<ReadonlySet<number>>(new Set());
  const list = filterLib(LIB, f, q);
  return (
    <section className={`${s.card} ${s.tbl}`} aria-label="Thư viện">
      <div className={`${s.trow} ${s.lrow} ${s.thead}`}>
        <span>{t('ed.lDoc')}</span><span>{t('ed.lType')}</span><span>{t('ed.lDept')}</span><span>{t('ed.lUpdated')}</span>
        <span className={s.lEnd} title={t('ed.quizTitle')}>{t('ed.quiz')}</span>
      </div>
      {list.map((d) => {
        const has = d.hasQuiz || gen.has(d.id);
        return (
          <div key={d.id} className={`${s.trow} ${s.lrow}`}>
            <b className={s.lName} title={d.sub}>{d.k}</b>
            <span><span className={s.pill} data-tone={TONE[d.type]}>{d.type}</span></span>
            <span className={s.lSub}>{d.dept}</span>
            <span className={s.lSub}>{d.upd}</span>
            <button
              type="button"
              className={`${s.cta} ${s.lGen}`}
              data-tone="go"
              onClick={() => {
                if (!d.hasQuiz) setGen(new Set(gen).add(d.id));
                toast({ msg: d.hasQuiz ? `Mở bài kiểm tra: ${d.k.split(' · ')[0]}` : `Ask HANA đã tạo 10 câu hỏi từ ${d.k.split(' · ')[0]}` });
              }}
            >
              {!has && <IconSparkles size={16} aria-hidden="true" />}{has ? 'Xem bài kiểm tra' : 'Tạo bằng Ask HANA'}
            </button>
          </div>
        );
      })}
      {!list.length && <div className={s.noHit}>{t('ed.noDoc')}</div>}
    </section>
  );
}

export function LibraryScreen() {
  const { state } = useEdu();
  const phone = useLayout() === 'phone';
  const [q, setQ] = useState('');
  const [f, setF] = useState<Filter>('all');
  return (
    <>
      <PageHeader title="Thư viện SOP · hướng dẫn · video" />
      {state === 'ready' && (
        <div className={s.page}>
          {!phone && <SearchRow placeholder="Tìm SOP, hướng dẫn, video…" q={q} onQ={setQ} sum={`${filterLib(LIB, f, q).length} tài liệu`} />}
          <div className={s.chips} role="group" aria-label="Lọc theo loại">
            {FILTERS.map((x) => <button key={x.k} type="button" aria-pressed={f === x.k} onClick={() => setF(x.k)}>{x.label} <span>{countByType(LIB, x.k)}</span></button>)}
          </div>
        </div>
      )}
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có tài liệu nào' : undefined}
        skeleton={<div className={s.skel}><Skeleton rows={6} /></div>}
      >
        <div className={s.page}><LibBody q={q} f={f} /></div>
      </StateView>
    </>
  );
}
